import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom'; // Using react-router-dom v6+
import axios from 'axios';
// Removed react-toastify imports

// Define FormMessageDisplay component (or import if it's in a separate file)
const FormMessageDisplay = ({ message, type }) => {
    if (!message) return null;
    // Ensure you have CSS for these classes in login_old.css
    const messageClass = type === 'error' ? 'form-message-error' : // You might use Bootstrap alert classes e.g., 'alert alert-danger'
        (type === 'success' ? 'form-message-success' : // e.g., 'alert alert-success'
            'form-message-info'); // e.g., 'alert alert-info'
    return (<div className={`form-message-display ${messageClass} mb-3`} role="alert">{message}</div>); // Added mb-3 for spacing
};


function ResetPassword() {
    // --- State Variables ---
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [token, setToken] = useState(null);
    // Re-introduce formMessage state
    const [formMessage, setFormMessage] = useState({ text: '', type: '' });
    const [isLoading, setIsLoading] = useState(false);

    // --- Hooks ---
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();

    // --- Effects ---
    // Effect to get the token from URL and load CSS
    useEffect(() => {
        const link = document.createElement("link");
        link.rel = "stylesheet";
        link.href = "/assets/css/login_old.css"; // Adjust path as needed
        document.head.appendChild(link);

        const urlToken = searchParams.get('token');
        if (urlToken) {
            setToken(urlToken);
            console.log("Reset Token Found:", urlToken);
        } else {
            // Use setFormMessage for missing token error
            setFormMessage({
                text: "Password reset token not found in URL. Please use the link from your email.",
                type: 'error'
            });
        }

        return () => {
            if (document.head.contains(link)) {
                document.head.removeChild(link);
            }
        };
    }, [searchParams]); // Only trigger effect based on searchParams

    // --- Event Handlers ---
    const handleResetSubmit = async (e) => {
        e.preventDefault();
        setFormMessage({ text: '', type: '' }); // Clear previous messages

        if (!token) {
            setFormMessage({ text: "Cannot reset password without a valid token.", type: 'error' });
            return;
        }
        if (!newPassword || !confirmPassword) {
            setFormMessage({ text: "Please enter and confirm your new password.", type: 'error' });
            return;
        }
        if (newPassword !== confirmPassword) {
            setFormMessage({ text: "Passwords do not match.", type: 'error' });
            return;
        }
        if (newPassword.length < 8) {
            setFormMessage({ text: "Password must be at least 8 characters long.", type: 'error' });
            return;
        }

        setIsLoading(true);
        setFormMessage({ text: 'Processing your request...', type: 'info' }); // Optional: show processing message

        try {
            const apiUrl = `${process.env.REACT_APP_API_BASE_URL}/vendors/reset-password`;
            const response = await axios.post(apiUrl, {
                token: token,
                newPassword: newPassword,
                confirmPassword: confirmPassword
            });

            const successMessage = response.data.message || "Password has been reset successfully! Redirecting to login...";
            setFormMessage({ text: successMessage, type: 'success' });

            setNewPassword('');
            setConfirmPassword('');

            // Redirect after a short delay to allow user to see the success message
            setTimeout(() => {
                navigate('/login');
            }, 3000); // 3 seconds delay

        } catch (err) {
            console.error("Password reset failed:", err);
            const errorMessage = err.response?.data?.message || "Failed to reset password. The token might be invalid, expired, or there was a server error.";
            setFormMessage({ text: errorMessage, type: 'error' });
        } finally {
            setIsLoading(false);
        }
    };

    // --- Render Logic ---
    return (
        <div className="login-page d-flex align-items-center justify-content-center min-vh-100">
            {/* ToastContainer removed */}
            <div className="container-fluid-1">
                <div className="row justify-content-center align-items-center">
                    <div className="col-lg-5 col-md-7 col-sm-9 col-11 d-flex flex-column justify-content-center right-section">
                        <div className="form-container login-form-sec">
                            <h2 className="login-form-sec-h2 login-account text-center mb-4">Set New Password</h2>

                            {/* Render FormMessageDisplay here */}
                            <FormMessageDisplay message={formMessage.text} type={formMessage.type} />

                            {/* Only show the form if a token exists (initial check handled by formMessage) */}
                            {/* If no token, the form won't render due to the error message above */}
                            {token && (
                                <form onSubmit={handleResetSubmit}>
                                    <div className="input-group login-filed-input mb-3">
                                         <input
                                            className="input-group login-filed-input" 
                                            type="password"
                                            placeholder="Enter New Password"
                                            value={newPassword}
                                            onChange={(e) => setNewPassword(e.target.value)}
                                            required
                                            disabled={isLoading}
                                        />
                                    </div>
                                    <div className="input-group login-filed-input mb-4">
                                         <input
                                            className="input-group login-filed-input" 
                                            type="password"
                                            placeholder="Confirm New Password"
                                            value={confirmPassword}
                                            onChange={(e) => setConfirmPassword(e.target.value)}
                                            required
                                            disabled={isLoading}
                                        />
                                    </div>
                                    <button type="submit" className="btn mt-0 w-100" disabled={isLoading || (formMessage.type === 'success')}> {/* Disable on success to prevent re-submit */}
                                        {isLoading ? 'Resetting Password...' : 'Reset Password'}
                                    </button>
                                </form>
                            )}
                            {/* If no token, show a message or specific UI instead of just hiding the form */}
                            {!token && !formMessage.text && ( // Show if no token AND no initial error message yet (e.g., during initial load)
                                <p className="text-center text-muted">Loading token information...</p>
                            )}


                             <div className="text-center mt-4">
                                 <a className="forgot-password" href="/login">Back to Login</a>
                             </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default ResetPassword;